// ArticleEditor.jsx - Admin article creation/editing interface
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Save, Eye, Send, ArrowLeft, Plus, Trash2, X,
  Image, FileText, Tag, Settings, Globe
} from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_URL || '';

const HERO_STYLES = [
  { value: 'default', label: 'Default (Light)' },
  { value: 'dark', label: 'Dark' },
  { value: 'gradient', label: 'Gradient' },
  { value: 'minimal', label: 'Minimal' },
  { value: 'stats', label: 'Stats (with metrics)' }
];

export default function ArticleEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState([]);
  const [activeTab, setActiveTab] = useState('content');

  // Article state
  const [article, setArticle] = useState({
    title: '',
    subtitle: '',
    content: '',
    excerpt: '',
    category: '',
    tags: [],
    hero_style: 'default',
    featured_image: '',
    featured_image_alt: '',
    author_name: 'Pro IRP Team',
    author_title: '',
    author_image: '',
    meta_title: '',
    meta_description: '',
    keywords: [],
    canonical_url: '',
    faqs: [],
    sources: [],
    cta_title: '',
    cta_description: '',
    cta_button_text: 'Start Free Trial',
    cta_button_url: '/signup',
    status: 'draft',
    scheduled_for: ''
  });

  // Tag input state
  const [tagInput, setTagInput] = useState('');
  const [keywordInput, setKeywordInput] = useState('');

  useEffect(() => {
    fetchCategories();
    if (isEditing) {
      fetchArticle();
    }
  }, [id]);

  const fetchCategories = async () => {
    try {
      const response = await fetch(`${API_BASE}/articles/categories`);
      const data = await response.json();
      if (data.ok) {
        setCategories(data.categories);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchArticle = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE}/articles/${id}`, {
        credentials: 'include'
      });
      const data = await response.json();
      if (data.ok) {
        setArticle({
          ...data.article,
          faqs: data.article.faqs || [],
          sources: data.article.sources || [],
          tags: data.article.tags || [],
          keywords: data.article.keywords || []
        });
      }
    } catch (error) {
      console.error('Error fetching article:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setArticle(prev => ({ ...prev, [field]: value }));
  };

  const handleAddTag = (e) => {
    if (e.key === 'Enter' && tagInput.trim()) {
      e.preventDefault();
      if (!article.tags.includes(tagInput.trim())) {
        handleChange('tags', [...article.tags, tagInput.trim()]);
      }
      setTagInput('');
    }
  };

  const handleRemoveTag = (tag) => {
    handleChange('tags', article.tags.filter(t => t !== tag));
  };

  const handleAddKeyword = (e) => {
    if (e.key === 'Enter' && keywordInput.trim()) {
      e.preventDefault();
      if (!article.keywords.includes(keywordInput.trim())) {
        handleChange('keywords', [...article.keywords, keywordInput.trim()]);
      }
      setKeywordInput('');
    }
  };

  const handleRemoveKeyword = (keyword) => {
    handleChange('keywords', article.keywords.filter(k => k !== keyword));
  };

  const handleAddFaq = () => {
    handleChange('faqs', [...article.faqs, { question: '', answer: '' }]);
  };

  const handleUpdateFaq = (index, field, value) => {
    const newFaqs = [...article.faqs];
    newFaqs[index] = { ...newFaqs[index], [field]: value };
    handleChange('faqs', newFaqs);
  };

  const handleRemoveFaq = (index) => {
    handleChange('faqs', article.faqs.filter((_, i) => i !== index));
  };

  const handleAddSource = () => {
    handleChange('sources', [...article.sources, { title: '', url: '', publisher: '' }]);
  };

  const handleUpdateSource = (index, field, value) => {
    const newSources = [...article.sources];
    newSources[index] = { ...newSources[index], [field]: value };
    handleChange('sources', newSources);
  };

  const handleRemoveSource = (index) => {
    handleChange('sources', article.sources.filter((_, i) => i !== index));
  };

  const handleSave = async (newStatus = null) => {
    try {
      setSaving(true);
      const payload = {
        ...article,
        status: newStatus || article.status
      };

      const url = isEditing
        ? `${API_BASE}/articles/${id}`
        : `${API_BASE}/articles`;

      const response = await fetch(url, {
        method: isEditing ? 'PUT' : 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      const data = await response.json();

      if (data.ok) {
        if (!isEditing) {
          navigate(`/admin/articles/${data.article.id}`);
        } else {
          setArticle(data.article);
        }
        alert(newStatus === 'published' ? 'Article published!' : 'Article saved!');
      } else {
        alert(`Error: ${data.error}`);
      }
    } catch (error) {
      console.error('Error saving article:', error);
      alert('Failed to save article');
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = () => handleSave('published');

  const handlePreview = () => {
    if (article.slug) {
      window.open(`/blog/${article.slug}`, '_blank');
    } else {
      alert('Save the article first to preview it');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/admin/articles')}
                className="p-2 text-gray-500 hover:text-gray-700"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <h1 className="text-lg font-semibold text-gray-900">
                {isEditing ? 'Edit Article' : 'New Article'}
              </h1>
              {article.status && (
                <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                  article.status === 'published'
                    ? 'bg-green-100 text-green-700'
                    : article.status === 'scheduled'
                    ? 'bg-yellow-100 text-yellow-700'
                    : 'bg-gray-100 text-gray-700'
                }`}>
                  {article.status}
                </span>
              )}
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={handlePreview}
                disabled={!article.slug}
                className="flex items-center gap-2 px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
              >
                <Eye className="w-4 h-4" />
                Preview
              </button>
              <button
                onClick={() => handleSave()}
                disabled={saving}
                className="flex items-center gap-2 px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                <Save className="w-4 h-4" />
                {saving ? 'Saving...' : 'Save Draft'}
              </button>
              <button
                onClick={handlePublish}
                disabled={saving || !article.title || !article.content}
                className="flex items-center gap-2 px-4 py-2 text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                <Send className="w-4 h-4" />
                Publish
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Tabs */}
        <div className="flex gap-1 mb-6 bg-gray-200 p-1 rounded-lg w-fit">
          {[
            { id: 'content', label: 'Content', icon: FileText },
            { id: 'seo', label: 'SEO', icon: Globe },
            { id: 'media', label: 'Media', icon: Image },
            { id: 'settings', label: 'Settings', icon: Settings }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                activeTab === tab.id
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {activeTab === 'content' && (
              <>
                {/* Title */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                  <input
                    type="text"
                    placeholder="Article Title"
                    value={article.title}
                    onChange={(e) => handleChange('title', e.target.value)}
                    className="w-full text-3xl font-bold border-0 focus:ring-0 placeholder-gray-300"
                  />
                  <input
                    type="text"
                    placeholder="Subtitle (optional)"
                    value={article.subtitle}
                    onChange={(e) => handleChange('subtitle', e.target.value)}
                    className="w-full text-lg text-gray-600 border-0 focus:ring-0 placeholder-gray-300 mt-2"
                  />
                </div>

                {/* Content */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Content (HTML supported)
                  </label>
                  <textarea
                    value={article.content}
                    onChange={(e) => handleChange('content', e.target.value)}
                    placeholder="Write your article content here. HTML tags are supported..."
                    rows={20}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono text-sm"
                  />
                </div>

                {/* FAQs */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-semibold text-gray-900">FAQs (for SEO schema)</h3>
                    <button
                      onClick={handleAddFaq}
                      className="flex items-center gap-1 text-blue-600 hover:text-blue-700 text-sm"
                    >
                      <Plus className="w-4 h-4" />
                      Add FAQ
                    </button>
                  </div>
                  {article.faqs.length === 0 ? (
                    <p className="text-gray-500 text-sm">No FAQs added. FAQs help with SEO through structured data.</p>
                  ) : (
                    <div className="space-y-4">
                      {article.faqs.map((faq, index) => (
                        <div key={index} className="bg-gray-50 rounded-lg p-4 relative">
                          <button
                            onClick={() => handleRemoveFaq(index)}
                            className="absolute top-2 right-2 text-gray-400 hover:text-red-500"
                          >
                            <X className="w-4 h-4" />
                          </button>
                          <input
                            type="text"
                            placeholder="Question"
                            value={faq.question}
                            onChange={(e) => handleUpdateFaq(index, 'question', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg mb-2 text-sm"
                          />
                          <textarea
                            placeholder="Answer"
                            value={faq.answer}
                            onChange={(e) => handleUpdateFaq(index, 'answer', e.target.value)}
                            rows={3}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Sources */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-semibold text-gray-900">Sources & Citations</h3>
                    <button
                      onClick={handleAddSource}
                      className="flex items-center gap-1 text-blue-600 hover:text-blue-700 text-sm"
                    >
                      <Plus className="w-4 h-4" />
                      Add Source
                    </button>
                  </div>
                  {article.sources.length === 0 ? (
                    <p className="text-gray-500 text-sm">No sources added.</p>
                  ) : (
                    <div className="space-y-4">
                      {article.sources.map((source, index) => (
                        <div key={index} className="bg-gray-50 rounded-lg p-4 relative">
                          <button
                            onClick={() => handleRemoveSource(index)}
                            className="absolute top-2 right-2 text-gray-400 hover:text-red-500"
                          >
                            <X className="w-4 h-4" />
                          </button>
                          <div className="grid grid-cols-2 gap-2">
                            <input
                              type="text"
                              placeholder="Title"
                              value={source.title}
                              onChange={(e) => handleUpdateSource(index, 'title', e.target.value)}
                              className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                            />
                            <input
                              type="text"
                              placeholder="Publisher"
                              value={source.publisher}
                              onChange={(e) => handleUpdateSource(index, 'publisher', e.target.value)}
                              className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                            />
                          </div>
                          <input
                            type="url"
                            placeholder="URL"
                            value={source.url}
                            onChange={(e) => handleUpdateSource(index, 'url', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm mt-2"
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}

            {activeTab === 'seo' && (
              <>
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 space-y-4">
                  <h3 className="font-semibold text-gray-900 mb-4">SEO Settings</h3>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Meta Title (max 60 chars)
                    </label>
                    <input
                      type="text"
                      value={article.meta_title}
                      onChange={(e) => handleChange('meta_title', e.target.value)}
                      placeholder="Leave blank to use article title"
                      maxLength={60}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      {article.meta_title.length}/60 characters
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Meta Description (max 160 chars)
                    </label>
                    <textarea
                      value={article.meta_description}
                      onChange={(e) => handleChange('meta_description', e.target.value)}
                      placeholder="Leave blank to auto-generate from excerpt"
                      maxLength={160}
                      rows={3}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      {article.meta_description.length}/160 characters
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Keywords
                    </label>
                    <input
                      type="text"
                      value={keywordInput}
                      onChange={(e) => setKeywordInput(e.target.value)}
                      onKeyDown={handleAddKeyword}
                      placeholder="Type keyword and press Enter"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    />
                    {article.keywords.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {article.keywords.map((keyword, index) => (
                          <span
                            key={index}
                            className="inline-flex items-center gap-1 px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm"
                          >
                            {keyword}
                            <button
                              onClick={() => handleRemoveKeyword(keyword)}
                              className="text-gray-400 hover:text-red-500"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Canonical URL
                    </label>
                    <input
                      type="url"
                      value={article.canonical_url}
                      onChange={(e) => handleChange('canonical_url', e.target.value)}
                      placeholder="Leave blank to use default URL"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>
                </div>
              </>
            )}

            {activeTab === 'media' && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 space-y-4">
                <h3 className="font-semibold text-gray-900 mb-4">Featured Image</h3>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Image URL
                  </label>
                  <input
                    type="url"
                    value={article.featured_image}
                    onChange={(e) => handleChange('featured_image', e.target.value)}
                    placeholder="https://example.com/image.jpg"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Alt Text
                  </label>
                  <input
                    type="text"
                    value={article.featured_image_alt}
                    onChange={(e) => handleChange('featured_image_alt', e.target.value)}
                    placeholder="Describe the image for accessibility"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  />
                </div>

                {article.featured_image && (
                  <div className="mt-4">
                    <p className="text-sm text-gray-500 mb-2">Preview:</p>
                    <img
                      src={article.featured_image}
                      alt={article.featured_image_alt || 'Preview'}
                      className="max-w-full h-48 object-cover rounded-lg"
                    />
                  </div>
                )}
              </div>
            )}

            {activeTab === 'settings' && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 space-y-4">
                <h3 className="font-semibold text-gray-900 mb-4">CTA Settings</h3>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    CTA Title
                  </label>
                  <input
                    type="text"
                    value={article.cta_title}
                    onChange={(e) => handleChange('cta_title', e.target.value)}
                    placeholder="Ready to Improve Your Retention?"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    CTA Description
                  </label>
                  <textarea
                    value={article.cta_description}
                    onChange={(e) => handleChange('cta_description', e.target.value)}
                    rows={2}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Button Text
                    </label>
                    <input
                      type="text"
                      value={article.cta_button_text}
                      onChange={(e) => handleChange('cta_button_text', e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Button URL
                    </label>
                    <input
                      type="text"
                      value={article.cta_button_url}
                      onChange={(e) => handleChange('cta_button_url', e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Status */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="font-semibold text-gray-900 mb-4">Status</h3>
              <select
                value={article.status}
                onChange={(e) => handleChange('status', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
              >
                <option value="draft">Draft</option>
                <option value="scheduled">Scheduled</option>
                <option value="published">Published</option>
                <option value="archived">Archived</option>
              </select>

              {article.status === 'scheduled' && (
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Publish Date
                  </label>
                  <input
                    type="datetime-local"
                    value={article.scheduled_for}
                    onChange={(e) => handleChange('scheduled_for', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
              )}
            </div>

            {/* Category */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="font-semibold text-gray-900 mb-4">Category</h3>
              <select
                value={article.category}
                onChange={(e) => handleChange('category', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
              >
                <option value="">Select category</option>
                {categories.map(cat => (
                  <option key={cat.slug} value={cat.slug}>
                    {cat.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Tags */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="font-semibold text-gray-900 mb-4">Tags</h3>
              <input
                type="text"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={handleAddTag}
                placeholder="Add tag and press Enter"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
              />
              {article.tags.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-3">
                  {article.tags.map((tag, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm"
                    >
                      <Tag className="w-3 h-3" />
                      {tag}
                      <button
                        onClick={() => handleRemoveTag(tag)}
                        className="text-blue-400 hover:text-red-500"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Hero Style */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="font-semibold text-gray-900 mb-4">Hero Style</h3>
              <select
                value={article.hero_style}
                onChange={(e) => handleChange('hero_style', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
              >
                {HERO_STYLES.map(style => (
                  <option key={style.value} value={style.value}>
                    {style.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Author */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="font-semibold text-gray-900 mb-4">Author</h3>
              <div className="space-y-3">
                <input
                  type="text"
                  value={article.author_name}
                  onChange={(e) => handleChange('author_name', e.target.value)}
                  placeholder="Author name"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                />
                <input
                  type="text"
                  value={article.author_title}
                  onChange={(e) => handleChange('author_title', e.target.value)}
                  placeholder="Author title"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                />
                <input
                  type="url"
                  value={article.author_image}
                  onChange={(e) => handleChange('author_image', e.target.value)}
                  placeholder="Author image URL"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                />
              </div>
            </div>

            {/* Excerpt */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="font-semibold text-gray-900 mb-4">Excerpt</h3>
              <textarea
                value={article.excerpt}
                onChange={(e) => handleChange('excerpt', e.target.value)}
                placeholder="Brief summary (auto-generated if left blank)"
                rows={4}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
